<template>
  <v-card class="chat-menu-card bg-surface rounded-xl border border-white-05 overflow-hidden" elevation="12">
    <v-card-title class="d-flex align-center justify-space-between pa-4 border-b">
      <span class="text-h6 font-weight-black">Private Messages</span>
      <div class="d-flex align-center">
        <v-btn
          icon="mdi-refresh"
          variant="text"
          size="small"
          :loading="loading"
          @click="fetchConversations()"
        ></v-btn>
        <v-btn
          :icon="showNewChatInput ? 'mdi-close' : 'mdi-message-plus-outline'"
          variant="text"
          size="small"
          :color="showNewChatInput ? 'error' : 'primary'"
          @click="showNewChatInput = !showNewChatInput"
        ></v-btn>
      </div>
    </v-card-title>

    <div v-if="showNewChatInput" class="pa-4 border-b bg-black-05">
      <v-text-field
        v-model="newChatHandle"
        label="Enter handle (e.g. gamedev.id)"
        placeholder="user.bsky.social"
        density="compact"
        variant="outlined"
        hide-details
        append-inner-icon="mdi-send"
        @click:append-inner="startNewChat"
        @keyup.enter="startNewChat"
        :loading="resolving"
        autofocus
        class="new-chat-input"
      ></v-text-field>
      <div v-if="errorMsg" class="text-caption text-error mt-1">{{ errorMsg }}</div>
    </div>

    <div v-if="loading && !conversations.length" class="text-center py-10">
      <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
    </div>

    <div v-else-if="!conversations.length" class="text-center py-12">
      <v-icon icon="mdi-message-off-outline" size="48" color="grey-darken-2" class="mb-4"></v-icon>
      <div class="text-grey">No messages found</div>
      <div class="text-caption text-grey-darken-1 px-8">Start a conversation via someone's profile</div>
    </div>

    <v-list v-else density="compact" class="pa-0 convo-list">
      <v-list-item
        v-for="convo in conversations"
        :key="convo.id"
        class="convo-item pa-4 border-b transition-all"
        @click="convo.unreadCount = 0; $emit('select-convo', convo)"
      >
        <template v-slot:prepend>
          <v-avatar size="48" class="mr-3 border border-white-05">
            <v-img v-if="getPeer(convo)?.avatar" :src="getPeer(convo).avatar"></v-img>
            <v-icon v-else icon="mdi-account"></v-icon>
          </v-avatar>
        </template>

        <v-list-item-title class="font-weight-black d-flex justify-space-between align-center">
          <span class="text-truncate">{{ getPeer(convo)?.displayName || getPeer(convo)?.handle }}</span>
          <span class="text-caption text-grey font-weight-regular">{{ formatTime(convo.lastMessage?.sentAt || convo.rev) }}</span>
        </v-list-item-title>

        <v-list-item-subtitle class="mt-1 d-flex align-center">
          <span :class="['text-truncate flex-grow-1', convo.unreadCount > 0 ? 'text-white font-weight-bold' : 'text-grey']">
            {{ convo.lastMessage?.text || 'Sent an attachment' }}
          </span>
          <v-chip v-if="convo.unreadCount > 0" color="primary" size="x-small" label class="ml-2 unread-badge">
            {{ convo.unreadCount }}
          </v-chip>
        </v-list-item-subtitle>
      </v-list-item>
    </v-list>

    <div v-if="cursor" class="text-center py-2">
      <v-btn variant="text" color="primary" size="small" :loading="loading" @click="fetchMore">Load more</v-btn>
    </div>
  </v-card>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { listConversations, currentUser } from '@/services/bluesky';

const emit = defineEmits(['select-convo']);

const conversations = ref<any[]>([]);
const loading = ref(false);
const cursor = ref<string | undefined>(undefined);
const showNewChatInput = ref(false);
const newChatHandle = ref('');
const resolving = ref(false);
const errorMsg = ref('');

const startNewChat = async () => {
  if (!newChatHandle.value || resolving.value) return;
  errorMsg.value = '';
  resolving.value = true;
  try {
    // We emit an event to App.vue to handle handle-to-convo logic if needed
    // or we do it here. Since App already has handleStartChat, let's expose it or do it here.
    // Better to do it here for immediacy.
    const { getOrCreateConvo, agent } = await import('@/services/bluesky');
    
    // 1. Resolve handle
    const handle = newChatHandle.value.replace(/^@/, '');
    const resolveRes = await agent.resolveHandle({ handle });
    const did = resolveRes.data.did;
    
    // 2. Get/Create convo
    const convo = await getOrCreateConvo([did]);
    emit('select-convo', convo);
    showNewChatInput.value = false;
    newChatHandle.value = '';
  } catch (err: any) {
    console.error('Failed to start chat:', err);
    errorMsg.value = 'User not found or DMs blocked';
  } finally {
    resolving.value = false;
  }
};

const fetchConversations = async (isMore = false) => {
  if (loading.value) return;
  loading.value = true;
  try {
    const res = await listConversations(20, isMore ? cursor.value : undefined);
    if (isMore) {
      conversations.value.push(...res.convos);
    } else {
      conversations.value = res.convos;
    }
    cursor.value = res.cursor;
  } finally {
    loading.value = false;
  }
};

const fetchMore = () => {
  if (cursor.value) fetchConversations(true);
};

const getPeer = (convo: any) => {
  return convo.members?.find((m: any) => m.did !== currentUser.value?.did);
};

const formatTime = (time: string) => {
  if (!time) return '';
  const date = new Date(time);
  const now = new Date();
  
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

onMounted(() => {
  fetchConversations();
});
</script>

<style scoped>
.chat-menu-card {
  width: 360px;
  max-width: 90vw;
  max-height: 500px;
  display: flex;
  flex-direction: column;
}

.convo-list {
  overflow-y: auto;
}

.convo-item:hover {
  background: rgba(var(--v-theme-primary), 0.05);
}

.unread-badge {
  font-size: 0.65rem !important;
  height: 18px !important;
  min-width: 18px !important;
  justify-content: center;
}

.border-b {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
</style>
